/**
 * BirFlyPermit — English strings. The canonical dictionary: `hi.ts` is typed against this file's
 * keys so the two can never drift apart silently.
 */
import type { Dict } from './index';

export const en: Dict = {
  // ── Navigation ─────────────────────────────────────────────────────────
  'nav.home': 'Home',
  'nav.register': 'Register',
  'nav.status': 'Check status',
  'nav.verify': 'Verify a card',
  'nav.office': 'Office',
  'nav.stats': 'Statistics',
  'nav.menu': 'Menu',
  'nav.about': 'About',
  'nav.staffSignIn': 'Staff sign in',
  'nav.primaryAria': 'Primary',
  'nav.footerAria': 'Footer',
  'nav.skipToContent': 'Skip to content',
  'footer.privacyNotice': 'Privacy notice',

  // ── Form steps ─────────────────────────────────────────────────────────
  'steps.who.title': 'Who you are',
  'steps.who.help': 'Your personal and contact details',
  'steps.photo.title': 'Your photo',
  'steps.photo.help': 'A clear, recent photo for your Free Flyer Card',
  'steps.documents.title': 'Your documents',
  'steps.documents.help': "Passport or Indian ID, and visa details if you're a foreign national",
  'steps.equipment.title': 'Your equipment',
  'steps.equipment.help': 'Glider, reserve parachute and certification details',
  'steps.equipment.certification': 'Flying licence / certification',
  'steps.equipment.reserveOptional': 'Reserve parachute (optional)',
  'steps.safety.title': 'Safety and emergency',
  'steps.safety.help': 'Insurance and an emergency contact',
  'steps.declaration.title': 'Declaration and bond',
  'steps.declaration.help': 'Read and accept the declaration and indemnity bond',

  // ── Field labels (one per PilotFields key) ───────────────────────────────
  'field.givenNames': 'Given name(s)',
  'field.surname': 'Surname',
  'field.fathersName': "Father's / Mother's name",
  'field.addressLine': 'Address',
  'field.city': 'City / Town',
  'field.region': 'State / Region',
  'field.country': 'Country of residence',
  'field.postcode': 'Postal code',
  'field.nationality': 'Nationality',
  'field.dob': "Your date of birth",
  'field.phone': 'Phone number',
  'field.email': 'Email address',
  'field.docType': 'Identity document type',
  'field.docNumber': 'Document number',
  'field.docIssuingCountry': 'Issuing country',
  'field.docExpiry': 'Document expiry date',
  'field.mrzLine1': 'MRZ line 1',
  'field.mrzLine2': 'MRZ line 2',
  'field.isForeign': 'Foreign national',
  'field.arrivalDate': 'Date of arrival in India',
  'field.visaType': 'Visa type',
  'field.visaValidFrom': 'Visa valid from',
  'field.visaValidTo': 'Visa valid to',
  'field.certBody': 'Certifying body',
  'field.certLevel': 'Certification level',
  'field.certNumber': 'Certificate number',
  'field.certIssued': 'Certificate issue date',
  'field.certExpiry': 'Certificate expiry date',
  'field.certHolderName': 'Name on certificate',
  'field.gliderModel': 'Paraglider model',
  'field.gliderClass': 'Glider class (EN rating)',
  'field.reserveMake': 'Reserve parachute make',
  'field.reserveModel': 'Reserve parachute model',
  'field.reserveRepack': 'Reserve last repacked',
  'field.insProvider': 'Insurance provider',
  'field.insPolicyNo': 'Insurance policy number',
  'field.insInsuredName': 'Name on insurance policy',
  'field.insCoverText': 'Insurance cover details',
  'field.insValidTo': 'Insurance valid to',
  'field.emgName': 'Emergency contact name',
  'field.emgRelationship': 'Emergency contact relationship',
  'field.emgPhone': 'Emergency contact phone',
  'field.bloodGroup': 'Blood group',
  'field.stayFrom': 'Staying in Bir from',
  'field.stayTo': 'Staying until',
  'field.stayPlace': 'Where are you staying in Bir?',
  'field.stayArea': 'Village / area of stay',
  'field.radioFrequency': 'Radio frequency (safety & rescue)',
  'field.declarationAccepted': 'Declarations',
  'field.bondAccepted': 'Indemnity bond',
  'field.typedName': 'Type your full name to sign',
  'field.consentPurpose': 'I consent to my data being used as described above',

  // ── Declaration clauses (verbatim from the paper form) ───────────────────
  'declaration.clause1': 'I am in good physical and mental health.',
  'declaration.clause2': 'I understand the nature of the sport and risk involved with flying paragliders in Himalayan conditions.',
  'declaration.clause3':
    'I choose to fly at my own risk and release the Himachal Pradesh Government administration and its appointed committees and assisting local bodies of any liability.',
  'declaration.clause4':
    'I further understand and declare that in case of any accident and search & rescue and medical treatment, it would be at my own cost.',

  // ── Indemnity bond clauses I–VIII — the wording of the office's printed INDEMNITY BOND
  //    (requirements/WhatsApp Image 2026-10-14), clause VIII added by hand by the reviewer ────
  'bond.clause1':
    'I have been informed and I am aware that paragliding can be dangerous and includes many risks and dangers, including but not limited to the hazards of travelling by paraglider, accident or illness in remote places without medical facilities, forces of nature, acts of God, extreme weather conditions, physical exertion for which I may not be prepared, and evacuation difficulties should I be injured or disabled. I have been informed and am aware of these and other inherent risks, and acknowledge that an enjoyment of adventuring beyond the normal safety of home and work is in part the reason for my participation.',
  'bond.clause2':
    'I hereby release, waive, indemnify and agree not to sue the operator, the Government of Himachal Pradesh, for all or any liability to me, my personal representatives, heirs, assigns and next of kin for any and all losses, damages or injuries, or any claim or demand on account of any injury to my person or property, or on account of my death, resulting from any cause including negligence of the operator or others, while I am participating in adventure. I further agree that I will assume the risk and will release the operator of any liability for any injury or damage to my body or property, or my death, due to any failure to obtain or administer appropriate rescue operations in any event of injury or mishap, including failure to obtain adequate medical services, to evacuate, or to supply treatment, medicine or trained rescue personnel.',
  'bond.clause3': 'I further agree that the State Government will not bear any expenditure incurred in rescue or medical care in case of accident or emergency.',
  'bond.clause4':
    'I expressly acknowledge and agree that adventure travel, including the trip in which I am participating, can be dangerous and involves serious and unprecedented bodily injury, property damage and death, and I intend the foregoing waiver and release of the operator to be as broad and inclusive as permitted by law. I am not relying on any oral or written representation or statement of the operator regarding safety, and I am entering this agreement of my own free will.',
  'bond.clause5': 'I further agree that I will have no liquor or illegal drugs in my possession when participating in the paragliding trip.',
  'bond.clause6': 'I have read and understood all the terms of this liability and assumption of risk and agree to it of my own free will and without reservation.',
  'bond.clause7': 'I further agree not to fly over the residence of His Holiness the Dalai Lama at McLeodganj (Upper Dharamshala).',
  'bond.clause8':
    'I further agree not to fly over or near the following areas: (1) the Army areas at Alhilal, Yol and Holta; (2) the Air Force radar at Dalhousie; (3) the Kullu airport at Bhuntar and its surroundings.',

  // ── Application status (pilot-friendly wording) ──────────────────────────
  'status.Submitted': 'Received — waiting for review',
  'status.Validated': 'Checks complete — waiting to be picked up for evaluation',
  'status.CorrectionRequested': 'Action needed — please provide the requested corrections',
  'status.ReadyForEvaluation': 'In queue for evaluation',
  'status.Evaluated': 'Evaluated — awaiting recommendation',
  'status.EvaluationFailed': 'Evaluation did not pass — under office review',
  'status.UnderReview': 'Under review by the SDM office',
  'status.PaymentPending': 'Approved — permit fee due',
  'status.Approved': 'Approved — your card is being issued',
  'status.CardIssued': 'Your Free Flyer Card has been issued',
  'status.Rejected': 'Application not approved',
  'status.Revoked': 'Card revoked',
  'status.Expired': 'Card expired',
  'status.greeting': 'Hello, {name}',

  // ── Verdict strings (mirror card.ts Verdict titles) ──────────────────────
  'verdict.valid': 'Valid',
  'verdict.validExpiresToday': 'Valid — expires today',
  'verdict.staleList': 'Valid — revocation list is {hours} h old',
  'verdict.notYetValid': 'Not yet valid',
  'verdict.expired': 'Expired',
  'verdict.revoked': 'REVOKED',
  'verdict.signatureInvalid': 'Signature invalid — card altered or forged',
  'verdict.unknownKey': 'Unknown signing key',
  'verdict.invalidCard': 'Invalid card',

  // ── Common buttons ─────────────────────────────────────────────────────
  'button.next': 'Next',
  'button.back': 'Back',
  'button.submit': 'Submit',
  'button.retry': 'Retry',
  'button.save': 'Save',
  'button.continueOffline': 'Continue offline',
  'button.confirm': 'Confirm',
  'button.cancel': 'Cancel',
  'button.close': 'Close',
  'button.download': 'Download',
  'button.print': 'Print',
  'button.upload': 'Upload',
  'button.edit': 'Edit',
  'button.delete': 'Delete',

  // ── Errors ─────────────────────────────────────────────────────────────
  'error.network': 'Network error — please check your connection and try again.',
  'error.validation': 'Please fix the highlighted fields.',
  'error.unauthorized': 'Please log in again.',
  'error.forbidden': "You don't have permission to do that.",
  'error.notFound': 'Not found.',
  'error.conflict': 'This record changed elsewhere — please reload and try again.',
  'error.rateLimited': 'Too many requests — please wait a moment and try again.',
  'error.internal': 'Something went wrong on our end. Please try again.',
  'error.tryAgain': 'Try again',
  'error.fixBefore': 'Before you can submit:',
  'error.typedNameRequired': 'Type your full name to sign.',
  'error.typedNameMismatch': 'This does not match the name you entered on step 1 ({name}). Type it the same way.',
  'error.declarationClauses': 'Tick all {n} declaration clauses.',
  'error.bondClauses': 'Tick all {n} indemnity bond clauses.',
  'error.consent': 'Tick the data-use consent.',
  'error.stepIncomplete': 'Step {n} ({title}) is incomplete.',
  'photo.check.retake': 'This photo cannot be used — retake it before continuing.',

  // ── DPDP purpose/consent statement ────────────────────────────────────────
  'consent.purpose':
    'Your information is collected only to register you as a pilot and issue the Free Flyer Card under the authority of the Sub-Divisional Magistrate. It is retained for {years} years and can be corrected or queried by contacting the grievance officer.',

  // ── Field hints — why the office needs each one ───────────────────────────
  'hint.givenNames': 'Exactly as printed on your identity document.',
  'hint.surname': 'Exactly as printed on your identity document.',
  'hint.fathersName': "Either parent's name, as on your document. Optional.",
  'hint.addressLine': 'Your permanent address, for the register and in case we need to reach you.',
  'hint.city': 'City or town of your permanent address.',
  'hint.region': 'State or region of your permanent address.',
  'hint.country': 'Country of your permanent address.',
  'hint.postcode': 'Postal code of your permanent address.',
  'hint.nationality': 'Determines whether visa fields are needed and how your name is checked.',
  'hint.dob': 'Checked for a plausible flying age and against your document.',
  'hint.phone': "So the office can reach you about your application. Include your country code.",
  'hint.email': "Optional, but you'll get your status link and acknowledgement by email.",
  'hint.docType': 'Foreign nationals must use a passport; Indian nationals may use any listed ID.',
  'hint.docNumber': 'Exactly as printed on your document.',
  'hint.docIssuingCountry': 'The country that issued your document.',
  'hint.docExpiry': 'Must stay valid through the end of the card period.',
  'hint.isForeign': 'Foreign nationals answer a few extra questions about their visa.',
  'hint.arrivalDate': 'Used to confirm your visa covers your stay.',
  'hint.visaType': 'The kind of visa you entered India on.',
  'hint.visaValidFrom': 'Must cover the whole card period.',
  'hint.visaValidTo': 'Must cover the whole card period.',
  'hint.certBody': 'Your paragliding certifying organisation.',
  'hint.certLevel': 'Determines the maximum glider class typically allowed.',
  'hint.certNumber': 'As printed on your certificate.',
  'hint.certIssued': 'Date your certification was issued.',
  'hint.certExpiry': 'Must not have expired.',
  'hint.certHolderName': 'Checked against your name — must be a close match.',
  'hint.gliderModel': 'e.g. Ozone Rush 6, Advance Iota 3',
  'hint.gliderClass': 'EN or DHV safety class — checked against your certification level.',
  'hint.reserveMake': 'Your reserve parachute, for the safety record.',
  'hint.reserveModel': 'Your reserve parachute, for the safety record.',
  'hint.reserveRepack': 'Reserves should be repacked periodically — helps us confirm it is airworthy.',
  'hint.insProvider': 'Your travel/adventure-sport insurance provider.',
  'hint.insPolicyNo': 'As printed on your policy.',
  'hint.insInsuredName': 'Checked against your name.',
  'hint.insCoverText': 'Paste or type the line of your policy that mentions paragliding — we check it covers the sport.',
  'hint.insValidTo': 'Must cover the whole card period.',
  'hint.emgName': "Someone we can contact if there's an emergency while you're flying.",
  'hint.emgRelationship': 'How this person is related to you.',
  'hint.emgPhone': 'Must be different from your own number.',
  'hint.bloodGroup': 'Optional — shared only with rescue teams if needed.',
  'hint.stayFrom': 'Your first flying day. Your card is valid from this date (or from the day it is issued, if later).',
  'hint.stayTo': 'Your last day in Bir. Your card is valid until this date. The permit fee is charged per 7 days of stay, and your documents (visa, insurance, ID) need to cover only these days.',
  'stay.feeHint': 'Permit fee for these dates: {text}',
  'hint.stayPlace': 'Hotel, guesthouse, homestay or camp name — the rescue team uses this to find you.',
  'hint.stayArea': 'Optional — e.g. Bir, Chaugan, Gunehar, Billing.',
  'hint.radioFrequency': 'Optional — the frequency you or your group monitor, e.g. 144.800 MHz. Used only by the rescue team in an emergency.',
  'hint.typedName': 'Type your full name exactly as given above — this is your signature.',
  'hint.consentPurpose': 'Required to submit your application.',

  // ── MRZ capture panel ──────────────────────────────────────────────────────

  // ── Photo capture ──────────────────────────────────────────────────────────
  'photo.capture.title': 'Take your card photo',
  'photo.capture.guide': 'Face the camera directly, plain background, head and shoulders inside the guide.',
  'photo.capture.guidance': 'Plain, light background. Face the camera, no hat or sunglasses, head and shoulders inside the outline.',
  'photo.capture.selfie': 'Take a selfie',
  'photo.capture.gallery': 'Choose from gallery',
  'photo.capture.button': 'Take or choose a photo',
  'photo.capture.retake': 'Retake',
  'photo.capture.zoom': 'Zoom',
  'photo.capture.confirm': 'Use this photo',
  'photo.capture.processing': 'Processing photo…',

  // ── File upload field labels (distinct from same-topic text fields) ──────────
  'upload.docLabel': 'Identity document photo or file',
  'upload.certLabel': 'Flying licence photo or file',
  'upload.insLabel': 'Insurance policy photo or file',
  'upload.photoLabel': 'Card photo',
  'upload.passportLabel': 'Passport photo page',
  'upload.passportHint': 'The page with your photo. Add the visa page too if you have one.',
  'upload.insHint': 'Add every page of your policy — several photos or one PDF.',
  'upload.takePhoto': 'Take a photo',
  'camera.title': 'Take a photo',
  'camera.capture': 'Capture',
  'camera.retake': 'Retake',
  'camera.use': 'Use this photo',
  'camera.switch': 'Switch camera',
  'camera.starting': 'Starting the camera…',
  'camera.denied': 'The camera could not be started. Allow camera access in your browser, or choose a file instead.',
  'camera.chooseInstead': 'Choose a file instead',
  'upload.chooseFile': 'Choose from gallery / files',
  'upload.addAnother': 'Add another page',
  'upload.multiHint': 'You can add several photos or a PDF — add every page of the document.',
  'upload.pagesAdded': '{n} pages added',
  'upload.pageOne': '1 page added',
  'upload.page': 'Page {n}',
  'upload.remove': 'Remove page {n}',
  'upload.tooLarge': 'This file is too large (max 1.4 MB). Try a photo of each page instead.',
  'upload.unsupported': 'This file type is not supported. Use a photo or a PDF.',

  // ── Document type / visa type options ───────────────────────────────────────
  'doc.type.passport': 'Passport',
  'doc.type.aadhaar': 'Aadhaar',
  'doc.type.voter_id': 'Voter ID',
  'doc.type.driving_licence': 'Driving licence',
  'doc.type.other': 'Other',
  'visa.type.eVisa': 'e-Visa',
  'visa.type.tourist': 'Tourist visa',
  'visa.type.other': 'Other',

  // ── Wizard chrome ─────────────────────────────────────────────────────────
  'wizard.progress': 'Step {n} of {total}',
  'wizard.resume.title': 'Resume your application?',
  'wizard.resume.body': "We found an application you started earlier on this device. Pick up where you left off, or start a fresh one.",
  'wizard.resume.resume': 'Resume',
  'wizard.resume.startOver': 'Start fresh',
  'wizard.uploadedFile': 'Uploaded: {name}',
  'wizard.requiredFile': 'This document is required.',
  'wizard.otherOption': 'Other',

  // ── Review step ──────────────────────────────────────────────────────────
  'review.title': 'Review your application',
  'review.edit': 'Edit',
  'review.stay': 'Stay: {from} – {to} ({days} days)',
  'review.fee.title': 'Permit fee — payable after the office approves',
  'review.fee.perWeek': '₹{unit} per 7 days × {weeks} week(s) = ₹{amount}',
  'review.fee.flat': '₹{amount}',
  'review.fee.none': 'No permit fee is currently charged.',
  'review.fee.loading': 'Calculating…',
  'review.fee.after': 'You pay only after approval — the e-mail will carry the payment details. The card is issued once the payment is confirmed.',
  'review.findingsTitle': 'Preliminary checks — the office re-runs these',
  'review.amberNotice': 'You can submit now, but the office will ask you to fix:',
  'review.readAll': 'I have read all the clauses above',
  'review.scrollHint': 'Scroll to the end to continue.',

  // ── Submission ─────────────────────────────────────────────────────────────
  'submit.offline.title': 'Saved — will send when you\'re back online',
  'submit.offline.body': "You're offline. Your application is saved on this device and will be sent automatically as soon as you have a connection.",
  'submit.sending': 'Sending your application…',
  'submit.success.title': 'Application received',
  'submit.success.reference': 'Your reference',
  'submit.success.body': 'Save this reference and the link below — you\'ll need them to check your status.',
  'submit.success.mailNote': 'Our e-mails come from {from}. If you do not see one within a few minutes, look in Spam or Promotions and mark it "Not spam" — then the next ones (approval, payment, card) arrive in your inbox.',
  'submit.success.copyLink': 'Copy status link',
  'submit.success.copied': 'Copied',
  'submit.success.shareLink': 'Share',
  'submit.success.downloadPdf': 'Download acknowledgement (PDF)',
  'submit.success.viewStatus': 'View my status',
  'submit.success.whatNext.title': 'What happens next',
  'submit.success.whatNext.1': 'The office checks your documents and details.',
  'submit.success.whatNext.2': 'An evaluator reviews your certification and equipment.',
  'submit.success.whatNext.3': 'The SDM approves and your Free Flyer Card is issued.',

  // ── Status page ─────────────────────────────────────────────────────────
  'status.page.title': 'Application {ref}',
  'status.page.checkTitle': 'Check your application status',
  'status.notFound.title': 'Application not found',
  'status.notFound.body': "We couldn't find that application with this link. Check the reference and link and try again.",
  'status.offline.title': "You're offline",
  'status.offline.body': 'Showing the last known status from {at}.',
  'status.findings.title': 'Findings summary',
  'status.correction.title': 'Action needed',
  'status.correction.body': 'The office has asked for the following before your application can proceed:',
  'status.correction.submit': 'Submit correction',
  'status.correction.sent': 'Correction sent — thank you.',
  'status.card.title': 'Your Free Flyer Card',
  'payment.title': 'Permit fee',
  'payment.amount': 'Amount',
  'payment.breakdown': '₹{unit} per 7 days × {weeks} week(s)',
  'payment.office.title': 'How to pay',
  'payment.office.after': 'Quote your reference when paying, then report the receipt or transaction number below.',
  'status.stay': 'Your stay in Bir',
  'status.stayDates': '{from} – {to}',
  'payment.dueBy': 'Pay by',
  'payment.status.pending': 'Not paid yet',
  'payment.status.claimed': 'Reported by you — awaiting the office\'s confirmation',
  'payment.status.paid': 'Paid',
  'payment.status.waived': 'Waived by the office',
  'payment.status.expired': 'The payment link has expired — ask the office to send a new one',
  'payment.payNow': 'Pay ₹{amount} securely',
  'payment.payNowHint': 'UPI, Indian cards and international cards are accepted on the secure Razorpay page. You will be brought back here afterwards.',
  'payment.checkAgain': 'Check payment status',
  'payment.upToDate': 'Status is up to date.',
  'payment.upi.title': 'Pay by UPI',
  'payment.upi.open': 'Open my UPI app',
  'payment.upi.vpa': 'UPI id',
  'payment.upi.payee': 'Payee',
  'payment.upi.hint': 'On your phone, tap the button; on a computer, scan the QR with any UPI app. Pay exactly ₹{amount} with reference {ref}, then report the UTR below.',
  'payment.upi.foreignHint': 'UPI needs an Indian bank account. If you cannot pay by UPI, contact the office: {phone}.',
  'payment.claim.title': 'Tell us you have paid',
  'payment.claim.reference': 'UTR / transaction id',
  'payment.claim.referenceHint': 'The UTR or transaction id — 12 digits in most UPI apps. Optional if you attach a screenshot.',
  'payment.claim.note': 'Note (optional)',
  'payment.claim.screenshot': 'Payment screenshot',
  'payment.claim.submit': 'Report payment',
  'payment.claim.done': 'Thank you — the office will confirm your payment and issue your card.',
  'payment.callback.verifying': 'Confirming your payment with the gateway…',
  'payment.callback.failed': 'We could not confirm the payment automatically yet. Tap "Check payment status" in a minute, or report it below.',
  'payment.receipt': 'Payment reference',
  'payment.paidAt': 'Paid on',
  'case.payment.title': 'Permit fee',
  'case.payment.route': 'Route',
  'case.payment.route.upi': 'UPI to the office account (officer confirms)',
  'case.payment.route.office': 'At the office / bank transfer (officer confirms)',
  'case.payment.route.razorpay': 'Razorpay payment link',
  'case.payment.claim': 'Pilot reported',
  'case.payment.claimNote': 'Pilot\'s note',
  'case.payment.screenshot': 'View screenshot',
  'case.payment.link': 'Payment link',
  'case.payment.refresh': 'Check with gateway',
  'case.payment.confirmedBy': 'Confirmed by',
  'case.payment.sent': 'Payment e-mail sent',
  'case.payment.times': '{n} time(s)',
  'payment.ways.title': 'Ways to pay',
  'payment.ways.amountDue': 'Amount to pay: ₹{amount}',
  'payment.ways.remark': 'Write your application reference {ref} in the remark / note of the payment, so the office can match it.',
  'payment.ways.intro': 'Choose any one of the ways below — the amount is the same for all of them.',
  'payment.ways.option.upi': 'Scan and pay by UPI',
  'payment.ways.option.bank': 'Bank transfer (NEFT / IMPS)',
  'payment.ways.option.cash': 'Cash at the office counter',
  'payment.ways.then': 'Paid by UPI or bank transfer? Tell us below so the office can match it in its account. Paid cash at the counter? The counter records it; you may still enter the receipt number below.',
  'payment.upi.official': 'The office\'s own bank-issued QR ({payee}) with the amount and your reference filled in. Works with GPay, PhonePe, Paytm, BHIM and every other UPI app.',
  'payment.upi.generic': 'Scan with any UPI app (GPay, PhonePe, Paytm, BHIM); the amount and your reference are filled in.',
  'payment.upi.openHint': 'On a phone, the button opens your UPI app with the payee, amount and reference already filled in.',
  'payment.bank.hint': 'Transfer from any Indian bank account by NEFT or IMPS. From abroad, ask your bank for a transfer to this account and allow a few working days.',
  'payment.cash.where': 'Pay at',
  'payment.cash.phone': 'Phone',
  'payment.cash.instructions': 'Counter hours and instructions',
  'payment.cash.hint': 'Carry your application reference {ref}. The counter gives you a receipt and records your payment on this application.',
  'payment.claim.receipt': 'Receipt number',
  'payment.claim.receiptHint': 'Printed on the receipt the counter gave you. Optional if you attach a photo of the receipt.',
  'payment.claim.receiptPhoto': 'Photo of the receipt',
  'payment.claim.rule.cash': 'Enter the receipt number (at least 3 characters) or attach a photo of the receipt.',
  'payment.bank.title': 'Bank transfer (NEFT / IMPS)',
  'payment.bank.accountName': 'Account name',
  'payment.bank.accountNumber': 'Account number',
  'payment.bank.ifsc': 'IFSC',
  'payment.bank.bankName': 'Bank',
  'payment.copy': 'Copy',
  'payment.copied': 'Copied',
  'payment.instructions.title': 'Instructions from the office',
  'payment.claim.intro': 'Paid already? Give us the details so the office can find your payment in its bank account.',
  'payment.claim.method': 'How did you pay?',
  'payment.claim.method.upi': 'UPI app',
  'payment.claim.method.bank': 'Bank transfer (NEFT, IMPS)',
  'payment.claim.method.cash': 'Cash at the office counter',
  'payment.claim.method.other': 'Other',
  'payment.claim.amount': 'Amount paid (₹)',
  'payment.claim.paidOn': 'Paid on',
  'payment.claim.payer': 'Name of the account holder who paid (optional)',
  'payment.claim.payerHint': 'Fill this in if someone else\'s account paid.',
  'payment.claim.rule': 'Enter the UTR (at least 5 characters) or attach a screenshot of the payment.',
  'payment.claim.screenshotAttached': 'Screenshot attached',
  'payment.claim.reported': 'What you reported',
  'payment.claim.reportedAt': 'Reported on',
  'payment.claim.edit': 'Made a mistake? Update your proof',
  'payment.claim.cancelEdit': 'Cancel',
  'payment.claim.keepFile': 'If you do not choose a new screenshot, the earlier one is kept.',
  'payment.rejected.title': 'The office could not match your payment',
  'payment.rejected.reason': 'Reason: {reason}',
  'payment.rejected.on': 'Sent back on {at}',
  'payment.rejected.advice': 'Please check the details and submit them again. If the transfer never went through, pay now using one of the ways above.',
  'case.payment.claim.method': 'Method',
  'case.payment.claim.amount': 'Amount reported',
  'case.payment.claim.paidOn': 'Paid on',
  'case.payment.claim.payer': 'Paid from account of',
  'case.payment.claim.at': 'Reported at',
  'case.payment.differs': 'differs from ₹{due}',
  'case.payment.verify': 'Open the office bank / UPI app and find a credit of ₹{due} on {date} with reference {ref}. Do not rely on the screenshot.',
  'case.payment.verify.reportedDate': 'the reported date',
  'case.payment.verify.noRef': '(none given)',
  'case.payment.payTo': 'Pilot was told to pay to',
  'case.payment.bank': 'Bank account',
  'case.payment.screenshot.loading': 'Loading screenshot…',
  'case.payment.screenshot.enlarge': 'Click to enlarge',
  'case.payment.screenshot.pdf': 'Open PDF',
  'case.payment.rejected.title': 'Sent back earlier',
  'case.payment.rejected.item': '{by} · {at}: {reason}',
  'case.payment.paidAmount': 'Amount received',
  'action.confirmPayment.claimHeading': 'Pilot reported',
  'action.confirmPayment.due': 'Amount due: ₹{due}',
  'action.confirmPayment.amountLabel': 'Amount received (₹)',
  'action.confirmPayment.verified': 'I have found this credit in the office bank statement / UPI app',
  'action.confirmPayment.verifiedCash': 'I have received this cash at the counter and issued a receipt for it',
  'action.confirmPayment.methodLabel': 'How was the money received?',
  'action.confirmPayment.receiptLabel': 'Counter receipt number',
  'action.confirmPayment.receiptHint': 'The number of the cash receipt issued at the counter — it goes on the pilot\'s payment record.',
  'action.rejectPaymentClaim.label': 'Payment not found — send back',
  'action.rejectPaymentClaim.title': 'Send the payment proof back to the pilot',
  'action.rejectPaymentClaim.reasonLabel': 'What could you not find or match?',
  'action.rejectPaymentClaim.reasonHint': '5–500 characters. The pilot is e-mailed this reason and can re-submit their proof.',
  'settings.manual.title': 'Manual payment route (UPI / bank transfer)',
  'settings.manual.description': 'Used when the route is upi or office. Money goes to the office\'s own account; an officer confirms it against the bank statement.',
  'settings.config.bank_account_name': 'Bank account name',
  'settings.config.bank_account_number': 'Bank account number (9–18 digits)',
  'settings.config.bank_ifsc': 'Bank IFSC, e.g. SBIN0001234',
  'settings.config.bank_name': 'Bank and branch',
  'settings.config.payment_provider.hint': 'Choose how pilots pay the permit fee.',
  'settings.config.fee_mode.hint': 'flat = one fee per application; per_week = per 7 days of stay.',
  'settings.config.payment_instructions.hint': 'Shown to the pilot on the upi and office routes. Line breaks are kept.',
  'settings.option.none': 'none — no permit fee',
  'settings.option.upi': 'upi — office UPI id, officer confirms',
  'settings.option.razorpay': 'razorpay — hosted payment link',
  'settings.option.office': 'office — counter / bank transfer, officer confirms',
  'settings.option.flat': 'flat — per application',
  'settings.option.per_week': 'per_week — per 7 days of stay',
  'status.card.showQr': 'Show QR full screen',
  'status.card.brightnessHint': 'Turn your screen brightness all the way up before scanning.',
  'status.card.download': 'Download card (PDF)',
  'status.card.addHome': 'Add this page to your home screen for offline access at the launch site.',
  'status.card.revoked': 'This card has been revoked and is no longer valid.',
  'status.myApplications': 'Your saved applications',

  // ── Timeline state explanations ───────────────────────────────────────────
  'status.explain.Submitted': 'We have received your application and are running initial checks.',
  'status.explain.Validated': 'Your checks are complete and your application is queued for evaluation.',
  'status.explain.CorrectionRequested': 'Please provide the corrections requested below.',
  'status.explain.ReadyForEvaluation': 'Your application is in the queue to be evaluated by our team.',
  'status.explain.Evaluated': 'Your certification and equipment have been evaluated; awaiting a recommendation.',
  'status.explain.EvaluationFailed': 'Evaluation did not pass — the SDM office is reviewing your application.',
  'status.explain.UnderReview': 'The SDM office is reviewing your application.',
  'status.explain.PaymentPending': 'Your application is approved. Pay the permit fee below and your card will be issued.',
  'status.explain.Approved': 'Your application is approved — your card is being issued.',
  'status.explain.CardIssued': 'Your Free Flyer Card has been issued. It is shown below.',
  'status.explain.Rejected': 'Your application was not approved.',
  'status.explain.Revoked': 'Your card has been revoked.',
  'status.explain.Expired': 'Your card has expired for this season.',

  // ── Office console (WP7) — chrome, guards, shortcuts ──────────────────────
  'office.nav.overview': 'Overview',
  'office.nav.queue': 'Queue',
  'office.nav.cases': 'Cases',
  'office.nav.dashboard': 'Dashboard',
  'office.nav.issue': 'Issue cards',
  'office.nav.staff': 'Staff',
  'office.nav.settings': 'Settings',
  'office.nav.exports': 'Exports',
  'office.nav.responder': 'Responder',
  'office.header.signOut': 'Sign out',
  'office.guard.signInTitle': 'Sign in required',
  'office.guard.signInBody': 'The office console is for SDM office staff. Sign in with your staff passphrase to continue.',
  'office.guard.goToSignIn': 'Go to staff sign in',
  'office.guard.notAllowedTitle': 'Not available for your role',
  'office.guard.notAllowedBody': "Your role ({role}) doesn't have access to this section.",
  'office.guard.sessionExpired': 'Your session expired — please sign in again.',
  'office.help.title': 'Keyboard shortcuts',
  'office.help.search': 'Focus search',
  'office.help.moveRows': 'Move between rows',
  'office.help.openRow': 'Open selected row',
  'office.help.closeModal': 'Close dialog / modal',
  'office.help.open': 'Show this help',

  // ── Office overview (/office) — the SDM/DC executive snapshot ────────────
  'office.overview.greeting.morning': 'Good morning, {name}',
  'office.overview.greeting.afternoon': 'Good afternoon, {name}',
  'office.overview.greeting.evening': 'Good evening, {name}',
  'office.overview.season.daysLeft': '{days} days left',
  'office.overview.season.lastDay': 'Last day',
  'office.overview.season.ended': 'Season ended',
  'office.overview.status.registerLive': 'Register live',
  'office.overview.status.backend': 'Backend: {backend} · online',
  'office.overview.status.backend.offline': 'Backend: offline',
  'office.overview.backend.appsscript': 'Apps Script',
  'office.overview.backend.devserver': 'Dev server',
  'office.overview.backend.demo': 'Demo',
  'office.overview.kpi.pending': 'Pending review',
  'office.overview.kpi.correction': 'Awaiting correction',
  'office.overview.kpi.correction.oldest': 'oldest {age}',
  'office.overview.kpi.ready': 'Ready to issue',
  'office.overview.kpi.issuedToday': 'Issued today',
  'office.overview.kpi.expiring': 'Expiring in 7 days',
  'office.overview.kpi.avgApproval': 'Average approval time',
  'office.overview.kpi.avgApproval.value': '{hours}h',
  'office.overview.kpi.avgApproval.none': 'No data yet',
  'office.overview.pipeline.title': 'Pipeline',
  'office.overview.pipeline.stage.submitted': 'Submitted',
  'office.overview.pipeline.stage.checked': 'Checked',
  'office.overview.pipeline.stage.evaluation': 'Evaluation',
  'office.overview.pipeline.stage.review': 'Review',
  'office.overview.pipeline.stage.payment': 'Awaiting payment',
  'office.overview.pipeline.stage.approved': 'Approved',
  'office.overview.pipeline.stage.issued': 'Issued',
  'office.overview.decision.title': 'Needs your decision',
  'office.overview.decision.andMore': 'and {count} more',
  'office.overview.decision.review': 'Review',
  'office.overview.decision.view': 'View',
  'office.overview.decision.empty.title': 'Nothing waiting on you',
  'office.overview.decision.empty.body': "There's nothing in your queue right now — check back later, or browse the full queue.",
  'office.overview.decision.assignedTo': 'Assigned to {name}',
  'office.overview.decision.unassigned': 'Unassigned',
  'office.overview.season.title': 'Season at a glance',
  'office.overview.season.registrationsByDay': 'Registrations, last 30 days',
  'office.overview.season.nationalitySplit': 'Indian vs. foreign',
  'office.overview.season.topNationalities': 'Top nationalities',
  'office.overview.season.tierMix': 'Risk tier mix',
  'office.overview.season.noData': 'No registrations yet this season.',
  'office.overview.activity.title': 'Activity',
  'office.overview.activity.empty': 'No activity yet this season.',
  'office.overview.activity.line': '{name} → {state}',
  'office.overview.quickActions.title': 'Quick actions',
  'office.overview.quickActions.registerOnBehalf': 'Register a pilot on their behalf',
  'office.overview.quickActions.issueCards': 'Issue cards',
  'office.overview.quickActions.verify': 'Verify a card',
  'office.overview.quickActions.printSheet': 'Print sheet',
  'office.overview.quickActions.publicStats': 'Public statistics',
  'office.overview.offline.body': 'Showing the last saved numbers from {time}.',
  'office.overview.error.title': 'Could not load the overview',
  'office.overview.error.retry': 'Try again',

  // ── Queue ──────────────────────────────────────────────────────────────
  'queue.title': 'Queue',
  'queue.description': 'Applications waiting on an office action, grouped by state.',
  'queue.view.mine': 'My queue',
  'queue.view.all': 'All',
  'queue.view.attention': 'Needs attention',
  'queue.view.ready': 'Ready to issue',
  'queue.view.issued': 'Issued',
  'queue.view.payment': 'Awaiting payment',
  'queue.payment.claimed': 'Proof submitted — verify',
  'queue.payment.sentBack': 'Sent back',
  'queue.filters.state': 'State',
  'queue.filters.tier': 'Risk tier',
  'queue.filters.nationality': 'Nationality',
  'queue.filters.indian': 'Indian',
  'queue.filters.foreign': 'Foreign',
  'queue.filters.assignedToMe': 'Assigned to me',
  'queue.filters.search': 'Search',
  'queue.filters.searchPlaceholder': 'Search name, ref, document, card…',
  'queue.table.age': 'Age',
  'queue.table.name': 'Name',
  'queue.table.nationality': 'Nationality',
  'queue.table.cert': 'Cert.',
  'queue.table.glider': 'Glider class',
  'queue.table.certGlider': 'Cert. / Class',
  'queue.table.tier': 'Findings',
  'queue.table.assigned': 'Assigned',
  'queue.table.state': 'State',
  'queue.bulk.selected': '{count} selected',
  'queue.bulk.recommendAll': 'Recommend all',
  'queue.bulk.approveAll': 'Approve all',
  'queue.bulk.running': 'Working on {done} of {total}…',
  'queue.bulk.done': 'Done: {ok} succeeded, {failed} failed.',
  'queue.refresh': 'Refresh',
  'queue.lastUpdated': 'Last updated {time}',
  'queue.empty.title': 'Nothing here',
  'queue.empty.body': 'No applications match this view and filter combination.',
  'queue.unassigned': 'Unassigned',

  // ── Case view ──────────────────────────────────────────────────────────
  'case.tabs.entries': 'Entries',
  'case.tabs.checks': 'Checks',
  'case.tabs.documents': 'Documents',
  'case.back': 'Back to queue',
  'case.notFound.title': 'Case not found',
  'case.notFound.body': "This application reference doesn't exist or you don't have access to it.",
  'case.copy': 'Copy',
  'case.copied': 'Copied',
  'case.whatsapp': 'WhatsApp',
  'case.email': 'Email',
  'case.riskTier': 'Risk tier',
  'case.rulesetVersion': 'Ruleset version',
  'case.section.identity': "Pilot's details",
  'case.section.documents': 'Identity document',
  'case.section.equipment': 'Equipment and certification',
  'case.section.safety': 'Safety, insurance and emergency contact',
  'case.section.stay': 'Stay in Bir and rescue radio',
  'case.override': 'Override',
  'case.override.title': 'Override finding',
  'case.override.reasonLabel': 'Reason for overriding this finding',
  'case.override.reasonHint': 'Recorded on the audit trail against your name.',
  'case.documents.doc': 'Identity document',
  'case.documents.photo': 'Card photo',
  'case.documents.cert': 'Certification card',
  'case.documents.ins': 'Insurance',
  'case.documents.none': 'Not uploaded',
  'case.documents.open': 'Open',
  'case.documents.openTab': 'Open in new tab',
  'case.documents.download': 'Download',
  'case.documents.driveFolder': 'Open this applicant\'s folder in Drive',
  'case.documents.driveFolderHint': 'needs the office Google account',
  'case.documents.zoomIn': 'Zoom in',
  'case.documents.zoomOut': 'Zoom out',
  'case.documents.rotate': 'Rotate',
  'case.documents.typedVsDocument': 'Typed vs. document',
  'case.documents.typed': 'Typed by pilot',
  'case.documents.mrz': 'From document (MRZ)',
  'case.timeline.title': 'Timeline & audit',
  'case.timeline.empty': 'No audit history yet.',
  'case.notes.title': 'Notes',
  'case.notes.placeholder': 'Add an internal note…',
  'case.notes.add': 'Add note',
  'case.notes.empty': 'No notes yet.',
  'case.conflict.title': 'This case changed',
  'case.conflict.body': 'Someone else updated this case while you were looking at it.',
  'case.conflict.reload': 'Reload',
  'case.print': 'Print case summary',
  'case.reload': 'Reload',
  'case.actionBar.empty': 'No actions available for your role on this case right now.',

  // ── Case actions (action bar + modals) ────────────────────────────────
  'action.assign.label': 'Assign',
  'action.assign.title': 'Assign this case',
  'action.assign.staffLabel': 'Assign to',
  'action.assign.staffTextLabel': 'Staff username',
  'action.note.label': 'Add note',
  'action.note.title': 'Add an internal note',
  'action.note.textLabel': 'Note',
  'action.requestCorrection.label': 'Request correction',
  'action.requestCorrection.title': 'Request a correction',
  'action.requestCorrection.itemsLabel': 'What needs fixing',
  'action.requestCorrection.messageLabel': 'Message to the pilot',
  'action.requestCorrection.messageHint': 'Optional — shown alongside the checklist on their status page.',
  'action.evaluate.label': 'Evaluate',
  'action.evaluate.title': 'Evaluation checklist',
  'action.evaluate.outcomeLabel': 'Outcome',
  'action.evaluate.outcome.pass': 'Pass',
  'action.evaluate.outcome.conditional': 'Conditional',
  'action.evaluate.outcome.fail': 'Fail',
  'action.evaluate.notesLabel': 'Notes',
  'action.evaluate.checklist.identity': 'Pilot identity matches document',
  'action.evaluate.checklist.certSighted': 'Certification card sighted',
  'action.evaluate.checklist.gliderMatch': 'Glider matches declared model and class',
  'action.evaluate.checklist.reserve': 'Reserve fitted and in date',
  'action.evaluate.checklist.gear': 'Harness and helmet acceptable',
  'action.evaluate.checklist.briefed': 'Pilot briefed on site rules and no-fly zones',
  'action.evaluate.checklist.fitToday': 'Weather/skill fit for today',
  'action.evaluate.checklist.insurance': 'Insurance sighted',
  'action.recommend.label': 'Recommend',
  'action.recommend.title': 'Send a recommendation',
  'action.recommend.recommendationLabel': 'Recommendation',
  'action.recommend.recommendation.approve': 'Recommend approval',
  'action.recommend.recommendation.reject': 'Recommend rejection',
  'action.recommend.noteLabel': 'Note',
  'action.approve.label': 'Approve',
  'action.approve.title': 'Approve this application',
  'action.approve.reasonLabel': 'Reason (optional)',
  'action.approve.warnNotice': 'This application still has unresolved warnings — you can approve anyway.',
  'action.reject.label': 'Reject',
  'action.reject.title': 'Reject this application',
  'action.reject.reasonLabel': 'Reason',
  'action.reject.reasonHint': 'At least 10 characters — shown to the pilot.',
  'action.revoke.label': 'Revoke card',
  'action.revoke.title': 'Revoke this card',
  'action.revoke.reasonLabel': 'Reason',
  'action.reissue.label': 'Reissue',
  'action.reissue.title': 'Reissue a card',
  'action.reissue.confirm': 'A new card will be allocated for this pilot from /issue. Continue?',
  'action.markPrinted.label': 'Mark printed',
  'action.markPrinted.title': 'Mark card as printed',
  'action.markPrinted.sheetLabel': 'Print sheet / batch id',
  'action.reopen.label': 'Reopen',
  'action.reopen.title': 'Reopen this application',
  'action.reopen.confirm': 'This sends the application back to the start of the queue. Continue?',
  'action.confirmPayment.label': 'Confirm payment',
  'action.confirmPayment.title': 'Confirm the permit fee was received',
  'action.confirmPayment.referenceLabel': 'UTR / transaction id',
  'action.confirmPayment.referenceHint': 'As shown in the bank statement or UPI app. Pre-filled from what the pilot reported — check it matches the money actually received.',
  'action.confirmPayment.noteLabel': 'Note (optional)',
  'action.waivePayment.label': 'Waive fee',
  'action.waivePayment.title': 'Waive the permit fee',
  'action.waivePayment.reasonLabel': 'Reason for waiving (required, recorded in the audit log)',
  'action.resendPaymentLink.label': 'Re-send payment details',
  'action.resendPaymentLink.title': 'Re-send the payment e-mail',
  'action.resendPaymentLink.confirm': 'The pilot receives a fresh e-mail with the payment link or UPI details and a new status link. An expired gateway link is re-created.',
  'action.override.label': 'Override',
  'action.reasonLabel': 'Reason',
  'action.submit': 'Submit',
  'action.success': '{action} done for {ref}.',

  // ── WhatsApp / email templates ─────────────────────────────────────────
  'template.correction.text': 'Hello {name}, this is the SDM Baijnath paragliding office regarding your Free Flyer Card application {ref}. We need a correction from you — please check your status page for details.',
  'template.approval.text': 'Hello {name}, good news — your Free Flyer Card application {ref} has been approved. Your card will be issued shortly.',
  'template.cardReady.text': 'Hello {name}, your Free Flyer Card for application {ref} is ready. Please visit the SDM office at Bir Billing to collect it.',

  // ── Evaluator screen ───────────────────────────────────────────────────
  'evaluator.title': "Today's evaluations",
  'evaluator.description': 'Applicants ready for a glider/certification check at the launch site.',
  'evaluator.search': 'Search name, ref or card number',
  'evaluator.scanCard': 'Scan card',
  'evaluator.empty.title': 'Nothing to evaluate right now',
  'evaluator.empty.body': 'New applicants will appear here as soon as they clear initial checks.',
  'evaluator.classWarning': 'Glider class exceeds this certification level',
  'evaluator.open': 'Open',
  'evaluator.back': 'Back to list',
  'evaluator.checklist.title': 'Assessment checklist',
  'evaluator.signature.title': 'Evaluator signature',
  'evaluator.signature.clear': 'Clear',
  'evaluator.signature.hint': 'Sign with your finger or stylus. Kept on this device only; a short hash is recorded with your notes as sig:<hash>.',
  'evaluator.signature.required': 'Please sign before submitting.',
  'evaluator.submit': 'Submit assessment',
  'evaluator.submitted': 'Assessment recorded for {ref}.',
  'evaluator.offline.queued': 'Saved offline — will send automatically once you reconnect.',
  'evaluator.outbox.pending': '{count} pending in outbox',
  'evaluator.outbox.flushed': 'Sent {count} queued assessment(s).',

  // ── Responder (emergency-only) view ────────────────────────────────────
  'responder.title': 'Emergency lookup',
  'responder.description': 'Search a pilot to see emergency contact details only.',
  'responder.search': 'Search name, card number or reference',
  'responder.notFound.title': 'No match',
  'responder.notFound.body': 'No pilot found for that search.',
  'responder.field.name': 'Name',
  'responder.field.nationality': 'Nationality',
  'responder.field.phone': 'Pilot phone',
  'responder.field.emgContact': 'Emergency contact',
  'responder.field.emgRelationship': 'Relationship',
  'responder.field.emgPhone': 'Emergency contact phone',
  'responder.field.bloodGroup': 'Blood group',
  'responder.field.stayPlace': 'Staying at',
  'responder.field.stayArea': 'Area',
  'responder.field.radioFrequency': 'Radio frequency',
  'responder.field.medicalValidTo': 'Medical insurance valid to',
  'responder.field.cardNo': 'Card number',
  'responder.call': 'Call',

  // ── Staff admin ────────────────────────────────────────────────────────
  'staff.title': 'Staff',
  'staff.description': 'Manage office accounts, roles and passphrases.',
  'staff.table.user': 'User',
  'staff.table.displayName': 'Display name',
  'staff.table.role': 'Role',
  'staff.table.active': 'Active',
  'staff.add': 'Add staff',
  'staff.edit': 'Edit staff',
  'staff.drawer.user': 'Username',
  'staff.drawer.displayName': 'Display name',
  'staff.drawer.role': 'Role',
  'staff.drawer.active': 'Active',
  'staff.drawer.setPassphrase': 'Set passphrase',
  'staff.drawer.passphrase1': 'New passphrase',
  'staff.drawer.passphrase2': 'Confirm passphrase',
  'staff.drawer.passphraseMismatch': "Passphrases don't match.",
  'staff.drawer.strength': 'Strength',
  'staff.drawer.strength.0': 'Very weak',
  'staff.drawer.strength.1': 'Weak',
  'staff.drawer.strength.2': 'Fair',
  'staff.drawer.strength.3': 'Strong',
  'staff.drawer.strength.4': 'Very strong',
  'staff.drawer.save': 'Save',
  'staff.saved': 'Staff record saved.',

  // ── Settings (admin) ───────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.description': 'Tenant configuration — changes take effect immediately.',
  'settings.warning': 'These settings affect the whole office. Double-check before saving.',
  'settings.config.season': 'Season',
  'settings.config.card_seq': 'Card sequence (read-only)',
  'settings.config.key_id': 'Active signing key',
  'settings.config.ruleset_version': 'Ruleset version',
  'settings.config.thresholds': 'Thresholds',
  'settings.config.payment_provider': 'Permit fee route: none, upi, razorpay or office',
  'settings.config.fee_inr_indian': 'Permit fee — Indian pilots (₹)',
  'settings.config.fee_inr_foreign': 'Permit fee — foreign pilots (₹)',
  'settings.config.payment_due_days': 'Days allowed to pay',
  'settings.config.upi_vpa': 'Office UPI id (upi route), e.g. sdmbaijnath@sbi',
  'settings.config.fee_mode': 'Fee rule: flat (per application) or per_week (per 7 days of stay)',
  'settings.config.fee_inr_per_week': 'Permit fee per 7 days of stay (₹) — per_week rule',
  'settings.config.payment_instructions': 'How to pay, shown to the pilot on the office route (counter hours, bank details …)',
  'settings.config.upi_payee_name': 'UPI payee name shown in the pilot\'s app',
  'settings.config.upi_qr_payload': 'Text of the office\'s printed UPI QR (optional)',
  'settings.config.upi_qr_payload.hint': 'Scan the bank\'s printed QR with any QR-reader app and paste the text it shows (it starts with upi://pay?pa=). Pilots then get that exact QR with the amount and reference filled in. Leave blank to build a plain QR from the UPI id.',
  'settings.qrPreview': 'Preview — scan it with your phone: it must open the same payee as the printed QR.',
  'settings.payments.title': 'Payment gateway (Razorpay)',
  'settings.payments.description': 'Paste the Key Id and Key Secret from the Razorpay dashboard (test keys start with rzp_test_). The webhook token is any long random text — paste the same value in Razorpay → Webhooks along with the URL below, subscribed to payment_link.paid. Values are stored in the office account\'s Script Properties and are never shown again.',
  'settings.payments.keyId': 'Key Id',
  'settings.payments.keySecret': 'Key Secret',
  'settings.payments.webhookToken': 'Webhook token',
  'settings.payments.webhookUrl': 'Webhook URL to paste in Razorpay',
  'settings.payments.save': 'Save gateway keys',
  'settings.payments.saved': 'Gateway keys saved.',
  'settings.payments.present': 'set',
  'settings.payments.absent': 'not set',
  'settings.payments.leaveBlank': 'Leave a field blank to keep its current value.',
  'settings.save': 'Save',
  'settings.saved': '{key} updated.',
  'settings.tenant.title': 'Tenant',
  'settings.demo.title': 'Demo data',
  'settings.demo.resetButton': 'Reset demo data',
  'settings.demo.resetConfirm': 'This wipes every demo application and reseeds fresh sample data. Continue?',
  'settings.demo.resetDone': 'Demo data reset.',
  'settings.drive.title': 'Documents on Google Drive',
  'settings.drive.description': 'Documents are filed as seasons / <season> / <month> / <day> / <SURNAME, Given names (reference)> with fixed names (photo.jpg, id-document.jpg, certificate.pdf, insurance.pdf, payment-proof.jpg). Applications filed before 17 Oct 2026 sat in flat per-reference folders; this moves them into place. Safe to run again.',
  'settings.drive.button': 'Tidy Drive folders now',
  'settings.drive.running': 'Tidying… {done} of {total} applications',
  'settings.drive.done': 'Done: {total} applications · {moved} files moved · {renamed} renamed · {unchanged} already in place · {missing} missing · {folders} empty folders removed',
  'settings.drive.root': 'Files live under: {path}',
  'settings.checks.title': 'Preliminary checks',
  'settings.checks.description': 'Re-runs the preliminary checks on every application that is not yet approved, using the current rules (ruleset {version}). An application that was held back only by a check that no longer fails moves on to the evaluation queue. Officer overrides are kept. Approved and issued applications are left as they were.',
  'settings.checks.button': 'Re-run checks now',
  'settings.checks.running': 'Re-running… {done} of {total} applications',
  'settings.checks.done': 'Done: {checked} re-checked · {changed} changed · {released} released to the evaluation queue · {skipped} already approved or issued (left as they were)',

  // ── Exports (admin/sdm) ────────────────────────────────────────────────
  'exports.title': 'Exports',
  'exports.description': 'Download the register, audit log and card list.',
  'exports.applicationsCsv': 'Applications (CSV)',
  'exports.auditCsv': 'Audit log (CSV)',
  'exports.cardsCsv': 'Cards (CSV)',
  'exports.json': 'Full export (JSON)',
  'exports.revocations.title': 'Revocation list',
  'exports.revocations.current': 'Currently published: version {version}, {age}',
  'exports.revocations.none': 'No revocation list has been published yet.',
  'exports.revocations.draft': 'Draft: version {version}, {count} card(s)',
  'exports.revocations.signLink': 'Sign & publish in /issue',

  // ── Short workflow-state labels (chart axis/category labels — see status.* for the longer, pilot-facing sentences) ──
  'queueState.Submitted': 'Submitted',
  'queueState.Validated': 'Validated',
  'queueState.CorrectionRequested': 'Correction requested',
  'queueState.ReadyForEvaluation': 'Ready for evaluation',
  'queueState.Evaluated': 'Evaluated',
  'queueState.EvaluationFailed': 'Evaluation failed',
  'queueState.UnderReview': 'Under review',
  'queueState.PaymentPending': 'Awaiting payment',
  'queueState.Approved': 'Approved',
  'queueState.CardIssued': 'Card issued',
  'queueState.Rejected': 'Rejected',
  'queueState.Revoked': 'Revoked',
  'queueState.Expired': 'Expired',

  // ── Charts (src/charts/* — generic chrome shared by every chart) ───────
  'charts.chartView': 'Chart',
  'charts.tableView': 'Table',
  'charts.downloadCsv': 'Download CSV',
  'charts.asOf': 'As of {time}',
  'charts.noData': 'No data yet',
  'charts.other': 'Other',
  'charts.count': 'Count',
  'charts.category': 'Category',

  // ── Dashboard (SDM/DC/admin/assistant, WP9) ─────────────────────────────
  'dashboard.eyebrow': 'Dashboard',
  'dashboard.title': 'Operations dashboard',
  'dashboard.tabs.aria': 'Dashboard view',
  'dashboard.tabs.operations': 'Operations',
  'dashboard.tabs.season': 'Season',
  'dashboard.guard.signInBody': 'Dashboards are for the SDM, DC and admin roles. Sign in with your staff passphrase to continue.',
  'dashboard.guard.notAllowedBody': "Your role ({role}) doesn't have access to the dashboard.",
  'dashboard.error.title': 'Could not load the dashboard',
  'dashboard.error.body': 'The last request failed. Check your connection and try again.',
  'dashboard.error.retry': 'Retry',
  'dashboard.offline.title': 'Showing the last saved numbers',
  'dashboard.offline.body': "You're offline — this is the last snapshot saved on this device, from {time}.",
  'dashboard.asOf': 'As of {time}',
  'dashboard.refresh': 'Refresh',

  'dashboard.ops.tile.pending': 'Pending review',
  'dashboard.ops.tile.correction': 'Awaiting correction',
  'dashboard.ops.tile.correction.oldest': 'oldest {age}',
  'dashboard.ops.tile.ready': 'Ready to issue',
  'dashboard.ops.tile.issuedToday': 'Issued today',
  'dashboard.ops.tile.expiring': 'Expiring in 7 days',
  'dashboard.ops.tile.avgApproval': 'Avg approval time',
  'dashboard.ops.tile.avgApproval.value': '{hours} h',
  'dashboard.ops.tile.avgApproval.none': 'No decisions yet',
  'dashboard.ops.queueByStage.title': 'Queue by stage',
  'dashboard.ops.queueByStage.subtitle': 'Applications currently in each stage, with the oldest waiting time',
  'dashboard.ops.queueByStage.annotation': 'oldest {age}',
  'dashboard.ops.trend.title': 'Submissions and cards issued',
  'dashboard.ops.trend.subtitle': 'Last 30 days',
  'dashboard.ops.trend.series.submissions': 'Submissions',
  'dashboard.ops.trend.series.issued': 'Cards issued',
  'dashboard.ops.tierMix.title': 'Risk tier mix',
  'dashboard.ops.tierMix.subtitle': 'Findings severity across every application this season',
  'dashboard.ops.tierMix.green': 'Green',
  'dashboard.ops.tierMix.amber': 'Amber',
  'dashboard.ops.tierMix.red': 'Red',
  'dashboard.ops.evaluators.title': 'Evaluations by evaluator',
  'dashboard.ops.evaluators.subtitle': 'Pass / fail split per evaluator',
  'dashboard.ops.evaluators.series.pass': 'Pass',
  'dashboard.ops.evaluators.series.fail': 'Fail',
  'dashboard.ops.evaluators.empty': 'No evaluations recorded yet.',
  'dashboard.ops.findings.title': 'Most common findings',
  'dashboard.ops.findings.subtitle': 'Failed rule checks, ranked by frequency',
  'dashboard.ops.findings.empty': 'No failed findings recorded yet.',
  'dashboard.ops.viewInQueue': 'View in queue',

  'dashboard.season.tile.pilots': 'Pilots registered',
  'dashboard.season.tile.cardsActive': 'Cards active',
  'dashboard.season.tile.nationality': 'Indian vs foreign',
  'dashboard.season.tile.countries': 'Countries represented',
  'dashboard.season.tile.avgApproval': 'Avg approval time',
  'dashboard.season.nationality.indian': 'Indian',
  'dashboard.season.nationality.foreign': 'Foreign',
  'dashboard.season.byWeek.title': 'Registrations by week',
  'dashboard.season.byWeek.subtitle': 'Applications submitted per ISO week',
  'dashboard.season.topNationalities.title': 'Top nationalities',
  'dashboard.season.topNationalities.subtitle': 'By number of applications this season',
  'dashboard.season.byState.title': 'Applications by state',
  'dashboard.season.byState.subtitle': 'Current mix across every workflow stage',
  'dashboard.season.summary.title': 'Season summary',
  'dashboard.season.summary.text':
    'So far this season {pilots} pilots from {countries} countries have registered; {active} hold an active card; average approval in {hours} h.',
  'dashboard.season.summary.textNoApproval':
    'So far this season {pilots} pilots from {countries} countries have registered; {active} hold an active card.',
  'dashboard.season.downloadCsv': 'Download season report (CSV)',
  'dashboard.season.print': 'Print',

  // ── Public statistics page (WP9, no login, no personal data) ───────────
  'stats.eyebrow': 'Public statistics',
  'stats.hero.title': '{season} at Bir Billing',
  'stats.hero.lede': "A public, no-login view of this season's Free Flyer Card registrations — updated from the register, with no personal data.",
  'stats.hero.pilots': 'Pilots registered',
  'stats.hero.cardsActive': 'Cards active',
  'stats.hero.countries': 'Countries represented',
  'stats.byWeek.title': 'Registrations by week',
  'stats.byWeek.subtitle': 'Applications submitted per ISO week, this season',
  'stats.nationalities.title': 'Where pilots come from',
  'stats.nationalities.subtitle': 'Top 10 nationalities this season',
  'stats.nationalities.other': 'Other',
  'stats.how.title': 'How this works',
  'stats.how.body':
    'Every pilot completes an in-person, ID-checked registration at Bir Billing and receives a Free Flyer Card once the SDM office approves it.',
  'stats.how.aboutLink': 'About BirFlyPermit',
  'stats.how.verifyLink': 'Verify a card',
  'stats.methodology': 'Aggregate counts only, updated from the register. No personal data is shown or stored on this page.',
  'stats.asOf': 'As of {time}',
  'stats.source.live': 'Live from the register',
  'stats.source.fallback': 'Showing the last published snapshot ({time})',
  'stats.share.title': 'Share this page',
  'stats.share.copy': 'Copy link',
  'stats.share.copied': 'Link copied',
  'stats.share.share': 'Share',
  'stats.error.title': 'Could not load statistics',
  'stats.error.retry': 'Retry',

  // ── Finding rule labels (plain-language, rule id → label; used by dashboard "top findings") ──
  'findings.rule.arrival.plausible': 'Arrival date implausible',
  'findings.rule.cert.expiry': 'Certification expired',
  'findings.rule.cert.holder': 'Certificate holder mismatch',
  'findings.rule.cert.recognised': 'Certification body not recognised',
  'findings.rule.declaration.complete': 'Declaration incomplete',
  'findings.rule.dob.plausible': 'Date of birth implausible',
  'findings.rule.doc.blocklist': 'Document on blocklist',
  'findings.rule.doc.duplicate': 'Duplicate document',
  'findings.rule.doc.expiry': 'Identity document expired',
  'findings.rule.emergency.distinct': 'Emergency contact not distinct',
  'findings.rule.glider.class': 'Glider class mismatch',
  'findings.rule.glider.known': 'Glider not recognised',
  'findings.rule.ins.covers': 'Insurance cover insufficient',
  'findings.rule.ins.validity': 'Insurance not valid',
  'findings.rule.medical.validity': 'Medical fitness not valid',
  'findings.rule.mrz.checkdigits': 'MRZ check digits failed',
  'findings.rule.mrz.format': 'MRZ format invalid',
  'findings.rule.name.match': 'Name mismatch',
  'findings.rule.phone.format': 'Phone number format invalid',
  'findings.rule.visa.covers': 'Visa does not cover stay',
  'findings.rule.photo.quality': 'Photo quality issue',

  // ── Issue (WP8: /issue) ──────────────────────────────────────────────────
  'issue.eyebrow': 'Issue',
  'issue.title': 'Issue Free Flyer Cards',
  'issue.description': 'Turn an approved application into a signed, printable card in one click.',
  'issue.tab.key': 'Office key',
  'issue.tab.cards': 'Issue cards',
  'issue.tab.sheet': 'Print sheet',
  'issue.tab.revocations': 'Revocations',
  'issue.keyNotReady.title': 'The office signing key is not set up yet',
  'issue.keyNotReady.body': 'Ask your administrator to open Office key and set it up once. After that, cards are signed automatically.',
  'issue.cards.action.confirmAndIssue': 'Confirm & issue',
  'issue.cards.modal.issuing': 'Issuing the card…',
  'issue.cards.field.cardNo': 'Card number',
  'issue.cards.field.validFrom': 'Valid from',
  'issue.cards.field.validTo': 'Valid to',
  'issue.cards.field.name': 'Name',
  'issue.cards.field.season': 'Valid for the season',
  'issue.cards.batch.confirmTitle': 'Issue all cards',
  'issue.cards.batch.confirmBody': 'Issue {count} cards now? Each will be signed and cannot be undone.',
  'issue.cards.batch.confirmAction': 'Issue {count} cards',
  'issue.revocations.action.publish': 'Publish revocation list',
  'issue.office.status.title': 'Office signing key',
  'issue.office.status.ready': 'Ready — key {kid}',
  'issue.office.status.notReady': 'Not set up',
  'issue.office.status.description': 'Cards are signed automatically by the office key, which is kept safely on the server. Nobody needs to unlock anything to issue a card.',
  'issue.office.keys.title': 'Keys',
  'issue.office.keys.none': 'No keys yet. Use Set up office key below.',
  'issue.office.col.name': 'Key name',
  'issue.office.col.created': 'Created',
  'issue.office.col.status': 'Status',
  'issue.office.badge.active': 'Active',
  'issue.office.badge.signs': 'Signs cards',
  'issue.office.badge.trusted': 'Trusted by the verifier app',
  'issue.office.notTrusted': 'Ask the engineer to publish this key to the verifier app (config/tenant.json) and redeploy, or cards will not verify.',
  'issue.office.actions.title': 'Key actions',
  'issue.office.action.setup': 'Set up office key',
  'issue.office.action.backupAgain': 'Print paper backup again',
  'issue.office.action.restore': 'Restore from paper backup',
  'issue.office.action.downloadBackup': 'Download paper backup (PDF)',
  'issue.office.setup.title': 'Set up the office key',
  'issue.office.setup.confirm': 'This creates a new office signing key and makes it the active one. The paper backup is shown only once, so have a printer ready. Continue?',
  'issue.office.setup.done': 'Office key created.',
  'issue.office.backup.title': 'Print paper backup again',
  'issue.office.backup.confirm': 'The paper backup can be used to sign cards in the office\'s name. Only continue if you will print it and store it safely. This request is recorded.',
  'issue.office.backup.shownTitle': 'Paper backup for key {kid}',
  'issue.office.backup.note': 'Print two copies, seal them, store one in the SDM safe. This is the only way to recover the key.',
  'issue.office.restore.title': 'Restore from paper backup',
  'issue.office.restore.help': 'Type the text from the paper backup and give the key a name.',
  'issue.office.restore.field.text': 'Text from the paper backup',
  'issue.office.restore.confirm': 'This puts the key back on the server and makes it the active key. Continue?',
  'issue.office.restore.fillAll': 'Enter a key name and the text from the paper backup.',
  'issue.office.restore.done': 'Key restored.',
  'settings.config.key_id.hint': 'Normally set automatically. Only change on the engineer\'s advice.',
  'case.documents.pages': '{label} · {count} pages',
  'case.documents.pageOf': 'Page {n} of {total}',


  'issue.cards.empty.title': 'Nothing to issue',
  'issue.cards.empty.body': 'No approved applications are waiting for a card right now.',
  'issue.cards.count': '{count} approved application(s)',
  'issue.cards.action.issueAll': 'Issue all ({count})',
  'issue.cards.action.issue': 'Issue',
  'issue.cards.action.printCard': 'Print this card',
  'issue.cards.batch.progress': 'Issued {done} of {total}',
  'issue.cards.batch.done': 'Batch issuance finished.',
  'issue.cards.modal.title': 'Issue card',
  'issue.cards.modal.confirmHelp': 'Check these details against the application — issuing cannot be undone.',
  'issue.cards.issueSuccess': 'Card {cardNo} issued.',
  'issue.cards.addedToSheet': 'Added to print sheet',

  'issue.printSheet.description': 'Cards issued but not yet printed. Select which ones to include, then download the 8-up A4 sheet.',
  'issue.printSheet.empty.title': 'Nothing to print',
  'issue.printSheet.empty.body': 'Every issued card has already been printed.',
  'issue.printSheet.action.download': 'Download print sheet ({count})',
  'issue.printSheet.action.markPrinted': 'Mark these as printed ({count})',
  'issue.printSheet.downloaded': 'Print sheet {sheetId} downloaded.',
  'issue.printSheet.markHelp': 'Once the sheet has printed correctly, mark these cards as printed so they drop off this list.',
  'issue.printSheet.markedPrinted': '{count} card(s) marked as printed.',

  'issue.revocations.published.title': 'Currently published',
  'issue.revocations.published.none': 'No revocation list has been published yet.',
  'issue.revocations.draft.title': 'Build and publish a new list',
  'issue.revocations.draft.description': 'Drafts the current set of revoked cards from the register. Review it, then publish — the list is signed automatically.',
  'issue.revocations.draft.adminOnly': 'Only an administrator can publish the revocation list.',
  'issue.revocations.action.buildDraft': 'Build draft',
  'issue.revocations.field.version': 'Version',
  'issue.revocations.field.generatedAt': 'Generated',
  'issue.revocations.field.age': 'Age',
  'issue.revocations.field.count': 'Revoked cards',
  'issue.revocations.field.signingKey': 'Signing key',
  'issue.revocations.ageHours': '{hours} h old',
  'issue.revocations.ageMinutes': '{minutes} min old',
  'issue.revocations.publishSuccess': 'Revocation list published.',
  'issue.revocations.instructions.title': 'How this reaches /verify',
  'issue.revocations.instructions.body':
    'The static revocations.json on the site is refreshed by the nightly job/deploy. The /verify page also fetches the latest list live from the backend whenever it has a signal, and always falls back to the last list it downloaded when offline.',

  // ── Verify (WP8: /verify) ─────────────────────────────────────────────────
  'verify.eyebrow': '{cardName} · {site}',
  'verify.title': 'Verify a Free Flyer Card',
  'verify.description': 'Scan the QR on a printed card to check it — works even without a signal.',
  'verify.camera.error': 'Could not start the camera.',
  'verify.camera.unavailable': 'Camera not available on this device — use the options below instead.',
  'verify.clockWarning': "This device's clock looks wrong — verdicts involving dates may be unreliable.",
  'verify.list.age': 'Revocation list: {age}',
  'verify.list.loading': 'Loading the revocation list…',
  'verify.torch.on': 'Turn on torch',
  'verify.torch.off': 'Turn off torch',
  'verify.fallback.title': 'Type or paste the card QR text',
  'verify.fallback.label': 'QR text',
  'verify.fallback.hint': 'If the camera cannot scan it, type or paste the exact text printed under the QR, or found in the QR payload.',
  'verify.fallback.action': 'Check',
  'verify.lookup.title': 'Enter a card number',
  'verify.lookup.help': 'Without the QR this can only check the revocation list — it cannot confirm the card is genuine or unexpired.',
  'verify.lookup.action': 'Look up',
  'verify.lookup.revoked': 'This card number has been revoked.',
  'verify.lookup.cannotConfirm': 'Not on the revocation list — but this cannot confirm the card is genuine or unexpired without scanning its QR.',
  'verify.settings.geo': 'Record location with scans',
  'verify.settings.sound': 'Sound',
  'verify.settings.mute': 'Mute sound',
  'verify.settings.unmute': 'Unmute sound',
  'verify.pendingScans': '{count} scan(s) waiting to upload',
  'verify.install.hint': 'Install this page for offline use — it works with no signal once installed.',
  'verify.install.action': 'Install',
  'verify.offlineNote': 'This page keeps working with no signal once it has loaded.',
  'verify.badge.green': 'Valid',
  'verify.badge.amber': 'Warning',
  'verify.badge.red': 'Invalid',
  'verify.result.validTo': 'Valid to {date}',
  'verify.result.comparePhoto': 'Compare the photo on the printed card with the person in front of you.',
  'verify.result.loadPhoto': 'Load photo',
  'verify.result.scanNext': 'Scan next',
  'verify.card.signedBy': 'Signed by {authority} · verified offline on this device',
  'verify.card.doNotAccept': 'Do not accept this card',
  'verify.card.photoPlaceholder': 'Compare the printed photo',
  'verify.card.nationality': 'Nationality',
  'verify.card.certification': 'Certification',
  'verify.card.validity': 'Validity',
  'verify.card.validFrom': 'From',
  'verify.card.validTo': 'Until',
  'verify.card.daysLeft': '{n} days left',
  'verify.card.dayLeft': '1 day left',
  'verify.card.expiresToday': 'expires today',
  'verify.card.expiredAgo': 'expired {n} days ago',
  'verify.card.expiredYesterday': 'expired yesterday',
  'verify.card.insurance': 'Insurance',
  'verify.card.insurer': 'Insurer',
  'verify.card.policyNo': 'Policy number',
  'verify.card.insuranceValidTo': 'Valid until',
  'verify.card.cover': 'Cover',
  'verify.card.more': 'more',
  'verify.card.less': 'less',
  'verify.card.emergency': 'Emergency contact',
  'verify.card.name': 'Name',
  'verify.card.relationship': 'Relationship',
  'verify.card.phone': 'Phone',
  'verify.card.call': 'Call',
  'verify.card.glider': 'Glider',
  'verify.card.gliderModel': 'Model',
  'verify.card.gliderClass': 'EN class',
  'verify.card.pilot': 'Pilot',
  'verify.card.dob': 'Date of birth',
  'verify.card.bloodGroup': 'Blood group',
  'verify.card.radioFrequency': 'Radio frequency',
  'verify.card.residence': 'Country of residence',
  'verify.card.office': 'Issuing office',
  'verify.card.officeAddress': 'Address',
  'verify.card.officeEmail': 'E-mail',
  'verify.card.officeWhatsapp': 'WhatsApp',
  'verify.card.grievance': 'Grievance officer',
  'verify.card.footerKey': 'Key {kid}',
  'verify.card.footerIssued': 'issued {date}',
  'verify.card.loading': 'Loading insurance and contact details…',
  'verify.card.offlineNote': 'Connect to the internet to see insurance and contact details.',
  'verify.card.revokedOn': 'The register says this card was revoked on {date}.',
  'status.card.openVerified': 'Open my verified card',

  // ── Login (staff sign-in) ─────────────────────────────────────────────────
  'login.eyebrow': 'Staff',
  'login.title': 'Sign in',
  'login.description': 'For Office of the SDM Baijnath staff — pilots do not need an account.',
  'login.field.user': 'User',
  'login.field.passphrase': 'Passphrase',
  'login.field.showPassphrase': 'Show',
  'login.field.hidePassphrase': 'Hide',
  'login.field.showPassphraseAria': 'Show passphrase',
  'login.field.hidePassphraseAria': 'Hide passphrase',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.signedInAs': 'Signed in as {name}',
  'login.error.generic': 'Could not sign in. Check your connection and try again.',
  'login.demo.title': 'Demo accounts',
  'login.demo.badge': 'demo build',
  'login.demo.intro': 'This build runs entirely in your browser with sample data. Sign in with any of these, using the passphrase {passphrase}:',

  // ── Home & About (marketing) ────────────────────────────────────────────────
  'home.eyebrow': '{office} · {district} District',
  'home.steps.register.title': 'Register online',
  'home.steps.register.body': 'Fill in your details from your phone, anywhere.',
  'home.steps.checked.title': 'Documents checked instantly',
  'home.steps.checked.body': 'Mistakes are caught before you submit.',
  'home.steps.evaluation.title': 'Evaluation & SDM approval',
  'home.steps.evaluation.body': 'Our team and the SDM confirm you can fly.',
  'home.steps.card.title': 'Your {cardName}',
  'home.steps.card.body': 'A signed QR you can show — even with no signal.',
  'home.needed.id': 'Passport / photo ID',
  'home.needed.photo': 'A passport-style photo',
  'home.needed.cert': 'Paragliding certification',
  'home.needed.insurance': 'Insurance covering paragliding',
  'home.needed.glider': 'Glider details',
  'home.needed.emergency': 'Emergency contact',
  'home.season.cta': 'Register for this season →',
  'home.trust.offline.title': 'Offline-verifiable',
  'home.trust.offline.body': 'Every card carries a signed QR that checks out with no signal at all.',
  'home.trust.dataMin.title': 'Data minimised',
  'home.trust.dataMin.body': "We collect only what's needed to register you and issue your card.",
  'home.trust.dpdp.title': 'DPDP notice',
  'home.trust.dpdp.link': 'Read our privacy notice →',
  'home.statusCheck.title': 'Check my status',
  'home.statusCheck.intro': 'Use the reference and token from your registration confirmation — or paste the status link we sent you.',
  'home.statusCheck.linkLabel': 'Status link',
  'home.statusCheck.linkHint': "Paste the full link — we'll fill in the fields below",
  'home.statusCheck.refLabel': 'Reference number',
  'home.statusCheck.tokenLabel': 'Access token',
  'home.statusCheck.submit': 'View status',
  'home.statusCheck.needToken': 'Enter the access token from your e-mail, or paste the whole status link (it ends in ?t=…).',
  'home.statusCheck.lost': 'Lost the e-mail?',
  'home.statusCheck.lostBody': 'Enter your reference and the e-mail address you registered with — we will send a fresh status link.',
  'home.statusCheck.emailLabel': 'E-mail address you registered with',
  'home.statusCheck.resend': 'Send me a new link',
  'home.statusCheck.resent': 'If the reference and e-mail match, a new link is on its way — check Spam too.',
  'home.heroArt.alt': 'Paragliders soaring above the Dhauladhar ridge at Bir Billing',

  'about.eyebrow': '{district} district · {state}',
  'about.title': 'About {name}',
  'about.description': '{name} is the digital {cardName} registry for visiting paraglider pilots at {site}, built for {authority}.',
  'about.howItWorks': 'How it works',
  'about.steps.register.title': 'Register online',
  'about.steps.register.body': 'Fill in your details, document, certification, glider and insurance from your phone — no office visit needed to start.',
  'about.steps.checked.title': 'Documents checked instantly',
  'about.steps.checked.body': 'Your document number, dates and certification are checked as you type, so mistakes are caught before you submit.',
  'about.steps.evaluation.title': 'Evaluation & SDM approval',
  'about.steps.evaluation.body': 'Our team confirms your certification and glider class against the season rules; the SDM approves the application.',
  'about.steps.card.title': 'Your {cardName}',
  'about.steps.card.body': 'You get a card with a signed QR code that can be verified offline at the launch site, on paper or on your phone.',
  'about.whatYouNeed': "What you'll need",
  'about.needed.id': 'Passport or other photo ID',
  'about.needed.photo': 'A passport-style photograph',
  'about.needed.cert': 'Your paragliding certification',
  'about.needed.insurance': 'Insurance covering paragliding',
  'about.needed.glider': 'Your glider make, model and class',
  'about.needed.emergency': 'An emergency contact',
  'about.yourData': 'Your data',
  'about.privacy.title': 'Data minimised, under the Digital Personal Data Protection Act',
  'about.privacy.body1':
    "We collect only what's needed to check your eligibility to fly and to issue your card: your identity document, certification, glider and insurance details, and an emergency contact. Your document is used to verify your identity and is not published; your card shows only your name, nationality, certification and a card number.",
  'about.privacy.body2':
    'Data is retained for {years} years as required for regulatory and safety purposes, and you may write to the grievance officer below to ask what we hold about you or to request a correction.',
  'about.contact': 'Contact',
  'about.grievanceOfficer': 'Grievance officer: {officer}',
  'about.builtFor': 'Built for {authority}.',
};
